/**
 * Invoice History Component (T076)
 * Invoice list with color-coded aging indicators
 */

import { useState } from 'react';
import { FileText, DollarSign } from 'lucide-react';
import { useCustomerInvoices } from '../../hooks/useCustomers';

interface InvoiceHistoryProps {
  customerId: string;
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatCurrency = (amount: string | number) => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
};

function getInvoiceStatusStyle(status: string, dueDate?: string) {
  const statusLower = status.toLowerCase();

  if (statusLower === 'paid') {
    return { label: 'Paid', className: 'claude-badge claude-badge-success' };
  }
  if (statusLower === 'cancelled' || statusLower === 'void') {
    return { label: status, className: 'claude-badge claude-badge-default' };
  }

  // Check aging for unpaid invoices
  if (dueDate) {
    const now = new Date();
    const due = new Date(dueDate);
    const daysUntilDue = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilDue < 0) {
      return { label: 'Overdue', className: 'claude-badge claude-badge-error' };
    }
    if (daysUntilDue <= 7) {
      return { label: 'Due Soon', className: 'claude-badge claude-badge-warning' };
    }
  }

  if (statusLower === 'sent' || statusLower === 'viewed') {
    return {
      label: status.charAt(0).toUpperCase() + status.slice(1),
      className: 'claude-badge bg-primary/10 text-primary border border-primary/20',
    };
  }

  return {
    label: status.charAt(0).toUpperCase() + status.slice(1),
    className: 'claude-badge claude-badge-default',
  };
}

export default function InvoiceHistory({ customerId }: InvoiceHistoryProps) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useCustomerInvoices(customerId, page, 20);

  const invoices = data?.items || [];
  const pagination = data?.pagination;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-center space-y-4">
          <div className="claude-spinner mx-auto"></div>
          <p className="text-muted-foreground">Loading invoices...</p>
        </div>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="claude-card">
        <div className="claude-empty-state">
          <FileText className="claude-empty-state-icon" />
          <h3 className="claude-empty-state-title">No invoices yet</h3>
          <p className="claude-empty-state-description">
            This customer doesn&apos;t have any invoices.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="claude-card overflow-hidden">
        <table className="claude-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Date</th>
              <th>Due Date</th>
              <th>Amount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice) => {
              const statusStyle = getInvoiceStatusStyle(invoice.status, invoice.dueDate);
              return (
                <tr key={invoice.id}>
                  <td>
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{invoice.invoiceNumber}</span>
                    </div>
                  </td>
                  <td className="text-muted-foreground">{formatDate(invoice.createdAt)}</td>
                  <td className="text-muted-foreground">
                    {invoice.dueDate ? formatDate(invoice.dueDate) : '—'}
                  </td>
                  <td>
                    <div className="flex items-center gap-1 font-medium">
                      <DollarSign className="w-3.5 h-3.5 text-muted-foreground" />
                      {formatCurrency(invoice.total)}
                    </div>
                  </td>
                  <td>
                    <span className={statusStyle.className}>{statusStyle.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {invoices.length} of {pagination.total} invoices
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1}
              className="claude-button claude-button-secondary text-sm"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={pagination.page >= pagination.pages}
              className="claude-button claude-button-secondary text-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
